import React from "react"
import { cn } from "@/lib/utils"
import { LucideIcon } from "lucide-react"
import { FloatingDots } from "@/components/ui/floating-dots"

interface HeroSectionProps {
  icon?: LucideIcon
  customIcon?: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  children?: React.ReactNode
  className?: string
  iconClassName?: string
  showSparkle?: boolean
  showGlow?: boolean
  showFloatingDots?: boolean
  background?: "default" | "gradient" | "solid"
  size?: "minimal" | "compact" | "standard" | "large"
  /** Heading level for SEO — 1 for main page title, 2 for secondary sections. */
  headingLevel?: 1 | 2 | 3
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  icon: Icon,
  customIcon,
  title,
  subtitle,
  children,
  className,
  iconClassName,
  showSparkle = true,
  showGlow = true,
  showFloatingDots = false,
  background = "default",
  size = "standard",
  headingLevel = 1,
}) => {
  const backgroundClasses = {
    default: "bg-linear-to-br from-green-50/50 via-white to-emerald-50/30",
    gradient: "bg-linear-to-br from-green-100 via-white to-emerald-100",
    solid: "bg-white"
  }

  const sizeClasses = {
    minimal: "pt-4 pb-1 md:pt-6 md:pb-2",
    compact: "py-6 md:py-8",
    standard: "py-6 md:py-12",
    large: "py-8 md:py-16 lg:py-20"
  }

  return (
    <section className={cn("hero-section relative", sizeClasses[size], className)}>
      {/* Background */}
      <div className={cn("hero-background", backgroundClasses[background])}></div>
      
      {/* Background Pattern - Simplified */}
      <div className="hero-pattern">
        <div className="absolute top-20 left-10 w-32 h-32 bg-green-200 rounded-full blur-3xl opacity-100"></div>
      </div>

      {/* Floating Dots - Contained within hero section */}
      {showFloatingDots && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <FloatingDots count={35} />
        </div>
      )}

      <div className="hero-container relative z-10">
        {/* Icon */}
        {(Icon || customIcon) && (
          <div className="hero-icon-container group">
            <div className="hero-icon">
              <div className={cn(iconClassName || "hero-icon-inner", "overflow-hidden")}>
                {customIcon ? (
                  <div className="flex items-center justify-center w-full h-full">
                    {customIcon}
                  </div>
                ) : Icon ? (
                  <Icon className="w-12 h-12 md:w-16 md:h-16 text-green-600 group-hover:scale-110 transition-transform duration-300" />
                ) : null}
              </div>
              {showSparkle && (
                <div className="hero-icon-sparkle animate-pulse-soft">
                  <svg className="w-full h-full" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
              )}
              {showGlow && <div className="hero-icon-glow"></div>}
            </div>
          </div>
        )}

        {/* Title */}
        {headingLevel === 1 && <h1 className="hero-title">{title}</h1>}
        {headingLevel === 2 && <h2 className="hero-title">{title}</h2>}
        {headingLevel === 3 && <h3 className="hero-title">{title}</h3>}

        {/* Subtitle - smaller margin when no CTA follows */}
        {subtitle && (
          <p className={cn("hero-subtitle", !children && "mb-4! md:mb-6!")}>
            {subtitle}
          </p>
        )}

        {/* CTA Section */}
        {children && <div className="hero-cta">{children}</div>}
      </div>
    </section>
  )
}

interface HeroTrustIndicatorProps {
  items: Array<{
    text: string
    delay?: number
  }>
  className?: string
}

export const HeroTrustIndicators: React.FC<HeroTrustIndicatorProps> = ({
  items,
  className
}) => {
  return (
    <div className={cn("w-full flex flex-wrap justify-center items-center gap-3 md:gap-4 mx-auto", className)}>
      {items.map((item, index) => (
        <div 
          key={index} 
          className="flex items-center justify-center space-x-2 bg-white/40 backdrop-blur-xs px-3 md:px-4 py-1.5 md:py-2 rounded-full border border-green-100/50 shadow-xs hover:shadow-md transition-all duration-300 whitespace-nowrap"
          style={{animationDelay: `${item.delay || index * 0.2}s`}}
        >
          <div 
            className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0"
          ></div>
          <span className="text-sm md:text-base font-medium">{item.text}</span>
        </div>
      ))}
    </div>
  )
}

interface HeroCTAProps {
  primaryButton?: React.ReactNode
  secondaryButton?: React.ReactNode
  className?: string
}

export const HeroCTA: React.FC<HeroCTAProps> = ({
  primaryButton,
  secondaryButton,
  className
}) => {
  return (
    <div className={cn("flex flex-col sm:flex-row gap-4 justify-center", className)}>
      {primaryButton}
      {secondaryButton}
    </div>
  )
} 
